import { Box, IconButton, Avatar, MenuItem, CircularProgress } from '@mui/material'
import { ChevronLeft, ChevronRight, MoreVert } from '@mui/icons-material'
import { PyrenzMenu, PyrenzModal, PyrenzModalContent, PyrenzBlueButton } from '@client/theme'
import { CharacterPayload, useChatSidebar } from '@client/components'
import { motion, AnimatePresence } from 'framer-motion'
import { useTheme } from '@mui/material'

interface ChatInfoSidebarProps {
  chat_uuid: string
  currentCharacter: CharacterPayload
}

export function ChatInfoSidebar({ chat_uuid, currentCharacter }: ChatInfoSidebarProps) {
  const theme = useTheme()
  const {
    recentChats,
    loading,
    anchorEl,
    collapsed,
    charModalOpen,
    modalLoading,
    setCollapsed,
    setCharModalOpen,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteChat,
    handleNewChat,
    handlePreviousChats,
  } = useChatSidebar(chat_uuid)

  if (loading) {
    return (
      <Box
        sx={{
          width: 280,
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <CircularProgress size={24} color="inherit" />
      </Box>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ width: collapsed ? 60 : 280 }}
        animate={{ width: collapsed ? 60 : 280 }}
        exit={{ width: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 30 }}
        style={{
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{
            height: '100%',
            bgcolor: theme.palette.background.paper + 'cc',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            flexDirection: 'column',
            backdropFilter: 'blur(12px)',
          }}
        >
          {!collapsed && (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              {recentChats.map((chat) => (
                <Box
                  key={chat.chat_uuid}
                  onClick={() => (window.location.href = `/chat/${chat.chat_uuid}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                    p: 1,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s ease',
                    '&:hover': { bgcolor: theme.palette.action.hover },
                    '&:hover .chat-menu-btn': {
                      opacity: 1,
                      transform: 'translateX(0)',
                    },
                  }}
                >
                  <Avatar src={chat.profile_image || undefined} sx={{ width: 36, height: 36 }}>
                    {chat.name?.[0] ?? '?'}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontWeight: 500,
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {chat.name}
                    </Box>

                    <Box
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {chat.lastMessage || 'No messages yet'}
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    className="chat-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleMenuOpen(e, chat)
                    }}
                    sx={{
                      opacity: 0,
                      transform: 'translateX(6px)',
                      transition: 'all 0.18s ease',
                      color: theme.palette.text.primary,
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              p: collapsed ? 0.5 : 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              position: 'relative',
            }}
          >
            {!collapsed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  flex: 1,
                  minWidth: 0,
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: theme.palette.action.hover, borderRadius: 1.5 },
                  p: 0.5,
                }}
                onClick={() => setCharModalOpen(true)}
              >
                <Avatar src={currentCharacter.profile_image || undefined} sx={{ width: 42, height: 42 }}>
                  {currentCharacter.name?.[0] ?? '?'}
                </Avatar>

                <Box
                  sx={{
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: theme.palette.text.primary,
                  }}
                >
                  {currentCharacter.name}
                </Box>
              </Box>
            )}

            <IconButton
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                position: collapsed ? 'static' : 'absolute',
                right: collapsed ? 'auto' : 8,
                width: 36,
                height: 36,
                color: theme.palette.text.primary,
              }}
            >
              {collapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>
        </Box>
      </motion.div>

      <PyrenzMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleNewChat()}>New Chat</MenuItem>
        <MenuItem onClick={handleDeleteChat} sx={{ color: '#ff7676' }}>
          Delete
        </MenuItem>
      </PyrenzMenu>

      <PyrenzModal
        open={charModalOpen}
        onClose={() => setCharModalOpen(false)}
        closeAfterTransition
        BackdropProps={{ timeout: 300 }}
      >
        <PyrenzModalContent>
          <Box sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
            {currentCharacter.name}
          </Box>

          <PyrenzBlueButton fullWidth sx={{ mb: 1 }} onClick={() => handleNewChat(currentCharacter.char_uuid)}>
            {modalLoading ? 'Creating...' : 'Create New Chat'}
          </PyrenzBlueButton>

          <PyrenzBlueButton
            fullWidth
            variant="outlined"
            sx={{ mb: 1 }}
            onClick={() => handlePreviousChats(currentCharacter.char_uuid)}
          >
            Previous Chats
          </PyrenzBlueButton>

          <PyrenzBlueButton fullWidth variant="text" onClick={() => setCharModalOpen(false)}>
            Close
          </PyrenzBlueButton>
        </PyrenzModalContent>
      </PyrenzModal>
    </AnimatePresence>
  )
}